import {
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  styled,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { FC, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import FormatListBulletedOutlinedIcon from "@mui/icons-material/FormatListBulletedOutlined";
import DisplaySettingsRoundedIcon from "@mui/icons-material/DisplaySettingsRounded";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import VpnKeyRoundedIcon from "@mui/icons-material/VpnKeyRounded";
import StorageRoundedIcon from "@mui/icons-material/StorageRounded";
import CategoryIcon from "@mui/icons-material/Category";
import LockClockRoundedIcon from "@mui/icons-material/LockClockRounded";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import VoiceChatIcon from "@mui/icons-material/VoiceChat";
import InsertChartOutlinedIcon from "@mui/icons-material/InsertChartOutlined";
import AddToHomeScreenOutlinedIcon from "@mui/icons-material/AddToHomeScreenOutlined";
import ChatBubbleOutlinedIcon from "@mui/icons-material/ChatBubbleOutlined";
import HttpsOutlinedIcon from "@mui/icons-material/HttpsOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import DashboardItemCardStyles from "./DashboardItemCardStyles";

const StyledCard = styled(Card)(({ theme }) => ({
  borderRadius: "14px",
  cursor: "pointer",
  display: "flex",
  flexDirection: "column",
  padding: 0,
  border: `1.5px solid ${theme.palette.divider}`,
  background: "rgb(255, 255, 255);",
  transition: "box-shadow 0.2s, border 0.2s",
  "&:hover": {
    boxShadow: "0 2px 12px 0 rgba(80,80,180,0.10)",
    border: `2px solid ${theme.palette.primary.light}`,
  },
}));

const StyledCardContent = styled(CardContent)(({ theme }) => ({
  padding: "18px 22px 18px 22px",
  minHeight: "120px",
  display: "flex",
  flexDirection: "column",
  gap: 8,
}));

interface IProps {
  title: string;
  description: string;
  navigationLink: string;
  iconColor: string;
  iconBgColor: string;
  badge?: string;
  badgeColor?: string;
}

const DashboardItemCard: FC<IProps> = ({
  title,
  description,
  navigationLink,
  iconColor,
  iconBgColor,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery("(max-width:767px)");
  const isActive = location.pathname === navigationLink;

  const handleCardRedirct = (link: string) => {
    if (navigationLink) {
      navigate(link);
    }
  };

  const getIcon = useCallback((title: string, iconColor: string) => {
    switch (title) {
      case "forms!":
        return <FormatListBulletedOutlinedIcon sx={{ fill: iconColor }} />;
      case "lookup":
        return <StorageRoundedIcon sx={{ fill: iconColor }} />;
      case "ui configuration":
        return <DisplaySettingsRoundedIcon sx={{ fill: iconColor }} />;
      case "user management":
        return <GroupAddOutlinedIcon sx={{ fill: iconColor }} />;
      case "secret keys":
        return <VpnKeyRoundedIcon sx={{ fill: iconColor }} />;
      case "features":
        return <CategoryIcon sx={{ fill: iconColor }} />;
      case "login reports":
        return <LockClockRoundedIcon sx={{ fill: iconColor }} />;
      case "content management":
        return <ArticleOutlinedIcon sx={{ fill: iconColor }} />;
      case "conversation analysis":
        return <VoiceChatIcon sx={{ fill: iconColor }} />;
      case "insure assist performance":
        return <InsertChartOutlinedIcon sx={{ fill: iconColor }} />;
      case "communication templates":
        return <AddToHomeScreenOutlinedIcon sx={{ fill: iconColor }} />;
      case "prompt management":
        return <ChatBubbleOutlinedIcon sx={{ fill: iconColor }} />;
      case "password rules configuration":
        return <HttpsOutlinedIcon sx={{ fill: iconColor }} />;
      case "customer activity analytics":
        return <InsightsOutlinedIcon sx={{ fill: iconColor }} />;
      case "roles & permissions":
        return <LockPersonOutlinedIcon sx={{ fill: iconColor }} />;
    }
  }, []);

  const cardId = `dashboard_card_${title.toLowerCase().replace(/\s+/g, "_")}`;

  return (
    <StyledCard
      onClick={() => handleCardRedirct(navigationLink)}
      sx={{
        ...DashboardItemCardStyles.StyledCard,
        ...(isActive && DashboardItemCardStyles.StyledCardActive),
      }}
      id={cardId}
      data-test-id={cardId}
      aria-label={`${title} dashboard card`}
      role="button"
      tabIndex={0}
    >
      <StyledCardContent>
        <Box
          sx={DashboardItemCardStyles.CardHeader}
          id={`${cardId}_header`}
          data-test-id={`${cardId}_header`}
          aria-label={`${title} card header`}
        >
          <IconButton
            style={{
              backgroundColor: iconBgColor,
              ...DashboardItemCardStyles.IconButton,
            }}
            size="small"
            id={`${cardId}_icon`}
            data-test-id={`${cardId}_icon`}
            aria-label={`${title} icon`}
            aria-hidden="true"
            disableRipple
            disableFocusRipple
          >
            {getIcon(title?.toLowerCase(), iconColor)}
          </IconButton>
          <Box sx={DashboardItemCardStyles.CardTitleContainer}>
            <Typography
              variant="h6"
              id={`${cardId}_title`}
              data-test-id={`${cardId}_title`}
            >
              {title}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <Typography
          variant="body1"
          color="text.secondary"
          sx={DashboardItemCardStyles.CardDescription}
          id={`${cardId}_description`}
          data-test-id={`${cardId}_description`}
        >
          {description}
        </Typography>
      </StyledCardContent>
    </StyledCard>
  );
};

export default DashboardItemCard;
